import { OverviewApiQuery, OverviewStatus } from '../../services/service-evaluations';
import {
  EvaluationLimits,
  EvaluationOverviewFilter,
  EvaluationOverviewSort,
  EvaluationOverviewSortValue,
  EvaluationSortDirection,
  EvaluationSortDirectionValue,
} from '../../util/evaluationContract';
import { EvaluationsView } from '../../util/evaluationsNavigation';

export type OverviewSort = { key: EvaluationOverviewSortValue; dir: EvaluationSortDirectionValue };

export type OverviewQuery = {
  /** None means every status. */
  statuses: OverviewStatus[];
  tags: string[];
  q: string;
  batch: string | null;
  supplier: string | null;
  /** 1-based, as the URL shows it; the API counts from 0. */
  page: number;
  /** The expanded row: a group key in Per projekt, a supplier id in Per leverantör. */
  open: string | null;
  /** None means latest activity first. */
  sort: OverviewSort | null;
};

export type OverviewFilters = Pick<OverviewQuery, 'statuses' | 'tags' | 'q' | 'batch' | 'supplier'>;

export const OVERVIEW_STATUSES: OverviewStatus[] = [
  EvaluationOverviewFilter.Ongoing,
  EvaluationOverviewFilter.Overdue,
  EvaluationOverviewFilter.Finished,
  EvaluationOverviewFilter.Unevaluated,
];

export const DEFAULT_OVERVIEW_QUERY: OverviewQuery = {
  statuses: [],
  tags: [],
  q: '',
  batch: null,
  supplier: null,
  page: 1,
  open: null,
  sort: null,
};

const OBJECT_ID = /^[0-9a-f]{24}$/i;
export const MAX_TAG_FILTERS = 20;
// The API takes a page of at most four digits, counted from 0.
const MAX_PAGE = 10000;

const idOrNull = (value: string | null) => (value && OBJECT_ID.test(value) ? value : null);

const pageOf = (value: string | null) => {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 && page <= MAX_PAGE ? page : 1;
};

const SORT_KEYS: string[] = Object.values(EvaluationOverviewSort);
const SORT_DIRECTIONS: string[] = Object.values(EvaluationSortDirection);

const sortOf = (key: string | null, dir: string | null): OverviewSort | null =>
  key && dir && SORT_KEYS.includes(key) && SORT_DIRECTIONS.includes(dir)
    ? { key: key as EvaluationOverviewSortValue, dir: dir as EvaluationSortDirectionValue }
    : null;

// A cut through an emoji leaves half of it, which no URL can carry.
const searchOf = (value: string) => {
  const cut = value.slice(0, EvaluationLimits.SearchMaxLength);
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
};

// One order for every list and every status as none, so one choice has one URL.
const statusesOf = (values: string[]) => {
  const statuses = OVERVIEW_STATUSES.filter(status => values.includes(status));
  return statuses.length === OVERVIEW_STATUSES.length ? [] : statuses;
};

export const toggleStatus = (statuses: OverviewStatus[], status: OverviewStatus) =>
  statusesOf(
    statuses.includes(status)
      ? statuses.filter(chosen => chosen !== status)
      : [...statuses, status],
  );

// Links shared before the chip was named ongoing still say active.
const withOngoingAlias = (value: string) =>
  value === 'active' ? EvaluationOverviewFilter.Ongoing : value;

// The URL is shared and hand-edited: anything the API would refuse falls back to its default.
export const parseOverviewQuery = (search: string): OverviewQuery => {
  const params = new URLSearchParams(search);
  return {
    statuses: statusesOf((params.get('status') ?? '').split(',').map(withOngoingAlias)),
    tags: (params.get('tags') ?? '')
      .split(',')
      .filter(id => OBJECT_ID.test(id))
      .slice(0, MAX_TAG_FILTERS),
    q: searchOf(params.get('q') ?? ''),
    batch: idOrNull(params.get('batch')),
    supplier: idOrNull(params.get('supplier')),
    page: pageOf(params.get('page')),
    open: params.get('open') || null,
    sort: sortOf(params.get('sort'), params.get('dir')),
  };
};

// Defaults are left out and keys sorted, so one state has one URL.
export const toSearch = (query: OverviewQuery): string => {
  const params: [string, string | null][] = [
    ['batch', query.batch],
    ['dir', query.sort?.dir ?? null],
    ['open', query.open],
    ['page', query.page > 1 ? String(query.page) : null],
    ['q', query.q || null],
    ['sort', query.sort?.key ?? null],
    ['status', query.statuses.length ? query.statuses.join(',') : null],
    ['supplier', query.supplier],
    ['tags', query.tags.length ? query.tags.join(',') : null],
  ];
  const parts = params
    .filter((param): param is [string, string] => Boolean(param[1]))
    .map(([key, value]) =>
      key === 'tags' || key === 'status'
        ? `${key}=${value}`
        : `${key}=${encodeURIComponent(value)}`,
    );
  return parts.length ? `?${parts.join('&')}` : '';
};

// What the API filters on: two queries with one key show the same rows.
export const filterKeyOf = (query: OverviewQuery): string =>
  JSON.stringify([
    statusesOf(query.statuses),
    [...query.tags].sort(),
    query.q.trim(),
    query.batch,
    query.supplier,
  ]);

// A focused supplier is the only row, so its open state outlives a filter or sort change.
const openAfterReset = (query: OverviewQuery, next: OverviewQuery) =>
  next.supplier && next.supplier === query.supplier ? next.open : null;

export const withFilter = (
  query: OverviewQuery,
  change: Partial<OverviewFilters>,
): OverviewQuery => {
  const next = { ...query, ...change };
  return filterKeyOf(next) === filterKeyOf(query)
    ? next
    : { ...next, page: 1, open: openAfterReset(query, next) };
};

export const withSort = (query: OverviewQuery, sort: OverviewSort | null): OverviewQuery => ({
  ...query,
  sort,
  page: 1,
  open: openAfterReset(query, query),
});

export const clearFilters = (query: OverviewQuery): OverviewQuery => ({
  ...query,
  ...DEFAULT_OVERVIEW_QUERY,
  sort: query.sort,
});

export const hasFilter = (query: OverviewQuery): boolean =>
  query.statuses.length > 0 ||
  query.tags.length > 0 ||
  Boolean(query.q.trim()) ||
  Boolean(query.batch) ||
  Boolean(query.supplier);

export const toApiQuery = (
  query: OverviewQuery,
  view: EvaluationsView,
  page: number,
  sort: OverviewSort | null = null,
): OverviewApiQuery => {
  const q = query.q.trim();
  return {
    ...(query.statuses.length ? { statuses: query.statuses } : {}),
    ...(query.tags.length ? { tagIds: query.tags } : {}),
    ...(q ? { q } : {}),
    ...(query.batch ? { batchId: query.batch } : {}),
    page,
    ...(view === 'supplier' && query.supplier ? { supplierId: query.supplier } : {}),
    ...(sort ? { sort: sort.key, dir: sort.dir } : {}),
  };
};
